import { useState, useEffect } from 'react';
import api from '../utils/api';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import EntityLink from '../components/EntityLink';
import PncPaymentsTab from '../components/PncPaymentsTab';

export default function BankReconciliationPage() {
  const [activeTab, setActiveTab] = useState('hexaware');
  const [unmatched, setUnmatched] = useState(null);
  const [error, setError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [activeTxn, setActiveTxn] = useState(null);

  function load() {
    setError(false);
    api.get('/bank-reconciliation/unmatched').then(r => setUnmatched(r.data)).catch(() => setError(true));
  }
  useEffect(load, []);

  async function handleUpload(file) {
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/bank-reconciliation/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(r.data.message);
      load();
    } catch (e) {
      setUploadResult(e.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function fmt(n) { return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`; }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Bank Reconciliation</h1>
          <p className="page-subtitle">View Hexaware payment history and match bank transactions to recorded payments.</p>
        </div>
        {activeTab === 'unmatched' && (
          <div className="page-actions">
            <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
              {uploading ? 'Uploading…' : '↑ Upload Bank Statement'}
              <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => e.target.files[0] && handleUpload(e.target.files[0])} />
            </label>
          </div>
        )}
      </div>

      {uploadResult && (
        <div style={{ background: 'var(--info-bg)', color: 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16 }}>
          {uploadResult}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid var(--border)' }}>
        {[
          { key: 'hexaware', label: '📥 Hexaware Payments' },
          { key: 'unmatched', label: '🔗 Unmatched Transactions' },
          { key: 'pnc', label: '🏦 PNC Payments' },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: activeTab === t.key ? 'var(--primary)' : 'transparent',
            color: activeTab === t.key ? '#fff' : 'var(--muted)',
            borderRadius: 'var(--radius) var(--radius) 0 0',
            borderBottom: activeTab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'hexaware' && <HexawarePaymentsTab />}

      {activeTab === 'pnc' && <PncPaymentsTab />}

      {activeTab === 'unmatched' && (
        <>
          {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
          {!error && unmatched === null && <StateBlock type="loading" />}
          {!error && unmatched !== null && unmatched.length === 0 && (
            <StateBlock type="empty" title="Nothing to reconcile" message="All bank transactions are matched, or none have been uploaded yet." />
          )}
          {!error && unmatched && unmatched.length > 0 && (
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Description</th><th>Ref #</th><th>Category</th><th>Direction</th><th>Amount</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
              </thead>
              <tbody>
                {unmatched.map(t => (
                  <tr key={t.id}>
                    <td>{t.transaction_date}</td>
                    <td>
                      {t.description || '—'}
                      {(t.creditor_name || t.debtor_name) && (
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{t.creditor_name || t.debtor_name}</div>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>{t.reference_number || '—'}</td>
                    <td>{t.category ? <Badge variant="info">{t.category}</Badge> : '—'}</td>
                    <td><Badge variant={t.direction === 'credit' ? 'success' : 'warning'}>{t.direction}</Badge></td>
                    <td>{fmt(t.amount)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-secondary" onClick={() => setActiveTxn(t)}>Find match</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {activeTxn && <MatchPanel txn={activeTxn} onClose={() => setActiveTxn(null)} onMatched={load} />}
        </>
      )}
    </div>
  );
}

function HexawarePaymentsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({ batch: '', invoice: '', consultant: '', from_date: '', to_date: '' });

  function load(f) {
    setLoading(true);
    setError(false);
    const params = new URLSearchParams();
    if (f.batch) params.append('batch', f.batch);
    if (f.invoice) params.append('invoice', f.invoice);
    if (f.consultant) params.append('consultant', f.consultant);
    if (f.from_date) params.append('from_date', f.from_date);
    if (f.to_date) params.append('to_date', f.to_date);
    api.get(`/ar-invoices/payments/hexaware?${params}`)
      .then(r => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load({}); }, []);

  function applyFilters() { load(filters); }

  function clearFilters() {
    const empty = { batch: '', invoice: '', consultant: '', from_date: '', to_date: '' };
    setFilters(empty);
    load({});
  }

  function fmt(n) { return n ? `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'; }

  const hasFilters = Object.values(filters).some(v => v);

  return (
    <div>
      {/* Filter Bar */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>BATCH #</label>
            <select value={filters.batch} onChange={e => setFilters(f => ({ ...f, batch: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }}>
              <option value="">All batches</option>
              {data && data.batches.map(b => <option key={b.pay_ref} value={b.pay_ref}>{b.pay_ref} ({b.pay_date})</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>INVOICE #</label>
            <input value={filters.invoice} onChange={e => setFilters(f => ({ ...f, invoice: e.target.value }))}
              placeholder="Search invoice #…"
              style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>CONSULTANT</label>
            <input value={filters.consultant} onChange={e => setFilters(f => ({ ...f, consultant: e.target.value }))}
              placeholder="Search consultant…"
              style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>FROM DATE</label>
            <input type="date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>TO DATE</label>
            <input type="date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))}
              style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            {hasFilters && <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>}
          </div>
        </div>
      </div>

      {loading && <StateBlock type="loading" />}
      {error && <StateBlock type="error" />}

      {!loading && !error && data && (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            {data.rows.length} payment lines &nbsp;|&nbsp;
            Total: <strong>{fmt(data.rows.reduce((s, r) => s + r.amount, 0))}</strong>
            &nbsp;|&nbsp; Matched: <strong>{data.rows.filter(r => r.consultant_name).length}</strong>
            &nbsp;|&nbsp; Unmatched: <strong style={{ color: 'var(--warning)' }}>{data.rows.filter(r => !r.consultant_name && r.link_status !== 'historical').length}</strong>
            &nbsp;|&nbsp; Historical: <strong>{data.rows.filter(r => !r.consultant_name && r.link_status === 'historical').length}</strong>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Consultant</th>
                <th>Batch #</th>
                <th>Pay Date</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>BGC</th>
                <th style={{ textAlign: 'right' }}>EPD</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.id} style={{ background: !r.consultant_name && r.link_status !== 'historical' ? 'rgba(255,180,0,0.05)' : undefined }}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}><EntityLink type="ar_invoice" id={r.ar_invoice_id}>{r.invoice_number}</EntityLink></td>
                  <td>{r.consultant_name ? <EntityLink type="contractor" id={r.contractor_id}>{r.consultant_name}</EntityLink> : <span title={r.note || undefined} style={{ color: 'var(--muted)', fontSize: 12, fontStyle: 'italic' }}>{r.link_status === 'historical' ? 'Historical (see note)' : 'Not matched'}</span>}</td>
                  <td style={{ fontSize: 12 }}>{r.pay_ref}</td>
                  <td style={{ fontSize: 12 }}>{r.pay_date}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.amount)}</td>
                  <td style={{ textAlign: 'right', color: r.bgc_amount > 0 ? 'var(--danger)' : 'var(--muted)' }}>
                    {r.bgc_amount > 0 ? fmt(r.bgc_amount) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', color: r.epd_amount > 0 ? 'var(--warning)' : 'var(--muted)' }}>
                    {r.epd_amount > 0 ? fmt(r.epd_amount) : '—'}
                  </td>
                  <td>
                    {r.consultant_name
                      ? <Badge variant="success">Matched</Badge>
                      : r.link_status === 'historical'
                        ? <Badge variant="neutral">Historical</Badge>
                        : <Badge variant="warning">Unmatched</Badge>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function MatchPanel({ txn, onClose, onMatched }) {
  const [suggestions, setSuggestions] = useState(null);
  const [matching, setMatching] = useState(false);
  const [people, setPeople] = useState([]);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ expense_type: 'business_expense', person_id: '', notes: '' });
  const [routing, setRouting] = useState(false);
  const [error, setError] = useState('');
  const [payMethod, setPayMethod] = useState('ach');

  useEffect(() => {
    api.get(`/bank-reconciliation/${txn.id}/suggestions`).then(r => setSuggestions(r.data));
    if (txn.direction === 'debit') api.get('/people').then(r => setPeople(r.data));
  }, [txn]);

  async function confirmMatch(candidate) {
    setMatching(true);
    try {
      await api.post(`/bank-reconciliation/${txn.id}/match`, { payment_type: candidate.payment_type, payment_id: candidate.id });
      onMatched();
      onClose();
    } finally { setMatching(false); }
  }

  async function recordInvoicePayment(invoice) {
    setMatching(true);
    setError('');
    try {
      await api.post(`/bank-reconciliation/${txn.id}/match-invoice`, { ar_invoice_id: invoice.id, payment_method: payMethod });
      onMatched();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not record the payment');
    } finally { setMatching(false); }
  }

  async function confirmExpenseRoute() {
    if (expenseForm.expense_type === 'person_expense' && !expenseForm.person_id) {
      setError('Select which person this expense belongs to');
      return;
    }
    setRouting(true);
    setError('');
    try {
      await api.post(`/bank-reconciliation/${txn.id}/route-expense`, expenseForm);
      onMatched();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not route this expense');
    } finally {
      setRouting(false);
    }
  }

  function fmt(n) { return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`; }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel">
        <div className="drawer-header">
          <h3 className="drawer-title">Find a match</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>
        <div className="panel-body">
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Bank transaction</div>
            <div>{txn.transaction_date} — {txn.description}</div>
            <div style={{ fontWeight: 700 }}>{fmt(txn.amount)} ({txn.direction})</div>
          </div>
          <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
            {txn.direction === 'credit' ? 'Candidate AR payments' : 'Candidate AP payments'}
          </h4>
          {suggestions === null && <div className="form-hint">Searching…</div>}
          {suggestions && suggestions.candidates.length === 0 && (
            <StateBlock type="empty" title="No close matches found" message="No unverified payment within $1.00 and 10 days of this transaction." />
          )}
          {suggestions && suggestions.candidates.map(c => (
            <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>
                  <EntityLink type={c.ar_invoice_id ? 'ar_invoice' : 'ap_invoice'} id={c.ar_invoice_id || c.ap_invoice_id}>{c.invoice_number}</EntityLink>
                  {' — '}{c.payment_date}
                </span>
                <span style={{ fontWeight: 600 }}>{fmt(c.cash_amount)}</span>
              </div>
              <button className="btn btn-primary" style={{ marginTop: 6, width: '100%' }} disabled={matching} onClick={() => confirmMatch(c)}>
                Confirm this match
              </button>
            </div>
          ))}
          {txn.direction === 'credit' && suggestions && suggestions.invoice_candidates?.length > 0 && (
            <>
              <h4 style={{ fontSize: 13, fontWeight: 700, marginTop: 20, marginBottom: 4 }}>Business Consulting invoices</h4>
              <div className="form-hint" style={{ marginBottom: 8 }}>Open consulting invoices matching this deposit by client, amount or invoice # in the reference. Confirming records the deposit as a verified payment (partial is fine).</div>
              {error && (
                <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 10 }}>{error}</div>
              )}
              <div className="form-field" style={{ marginBottom: 8 }}>
                <label>Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                  <option value="ach">ACH</option>
                  <option value="wire">Wire</option>
                  <option value="check">Check</option>
                </select>
              </div>
              {suggestions.invoice_candidates.map(c => (
                <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                      <EntityLink type="ar_invoice" id={c.id}>{c.invoice_number}</EntityLink>
                      {' — '}{c.client_name}
                    </span>
                    <span style={{ fontWeight: 600 }}>{fmt(c.outstanding)} due</span>
                  </div>
                  <div className="form-hint">Matched on: {c.match_reasons.join(', ')}</div>
                  <button className="btn btn-primary" style={{ marginTop: 6, width: '100%' }} disabled={matching} onClick={() => recordInvoicePayment(c)}>
                    Record {fmt(txn.amount)} on this invoice
                  </button>
                </div>
              ))}
            </>
          )}
          {txn.direction === 'debit' && (
            <>
              <h4 style={{ fontSize: 13, fontWeight: 700, marginTop: 20, marginBottom: 10 }}>Or route as an expense</h4>
              {!showExpenseForm ? (
                <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setShowExpenseForm(true)}>
                  This isn't a vendor payment — it's an expense
                </button>
              ) : (
                <div>
                  {error && (
                    <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 10 }}>
                      {error}
                    </div>
                  )}
                  <div className="form-field" style={{ marginBottom: 10 }}>
                    <label>Expense type</label>
                    <select value={expenseForm.expense_type} onChange={e => setExpenseForm(f => ({ ...f, expense_type: e.target.value, person_id: '' }))}>
                      <option value="business_expense">Business Expense (company-level)</option>
                      <option value="person_expense">Person Expense (tagged to someone)</option>
                    </select>
                  </div>
                  {expenseForm.expense_type === 'person_expense' && (
                    <div className="form-field" style={{ marginBottom: 10 }}>
                      <label>Person</label>
                      <select value={expenseForm.person_id} onChange={e => setExpenseForm(f => ({ ...f, person_id: e.target.value }))}>
                        <option value="">Select who this expense belongs to…</option>
                        {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                      </select>
                      <span className="form-hint">This reduces their ledger balance.</span>
                    </div>
                  )}
                  <div className="form-field" style={{ marginBottom: 10 }}>
                    <label>Note</label>
                    <input value={expenseForm.notes} onChange={e => setExpenseForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. laptop purchase, travel to client" />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowExpenseForm(false)}>Cancel</button>
                    <button className="btn btn-primary" style={{ flex: 1 }} onClick={confirmExpenseRoute} disabled={routing}>
                      {routing ? 'Routing…' : 'Route this expense'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
